import { ThemeId } from './ThemeId';
const gradients = {
    primary: 'linear-gradient(180deg, #00BAFF 0%, #33CCFD 83.85%)',
    'primary-hovered': 'linear-gradient(180deg, #00A2FF 0%, #40CBFF 100%)',
    underline: 'linear-gradient(180deg, #33CCFD 0%, #00A2FF 100%)',
};
const colors = {
    sky: '#00baff',
    sea: '#00a2ff',
    light: '#f5f7fa',
    cloud: '#F2F4F7',
    fog: '#e9eef5',
    silver: '#D3D5D9',
    pidgeon: '#ABBCC9',
    lightSlathe: '#7990a1',
    slathe: '#4D5C70',
    graphite: '#202730',
    success: '#00c662',
    error: '#f62f36',
    warning: '#FF8D1B',
    sun: '#ffea01',
    sunset: '#FFD200',
    ice: '#B1D5F1',
    white: '#ffffff',
    info: '#1887E8',
};
const radii = {
    none: '0px',
    xs: '2px',
    sm: '4px',
    md: '6px',
    lg: '8px',
    base: '12px',
    xl: '16px',
    '2xl': '24px',
};
export const defaultTheme = {
    id: ThemeId.Default,
    typography: {
        fontFamily: 'source-sans-basic, Source Sans Pro, helvetica, arial, sans-serif',
        variants: {
            'header-1': {
                fontSize: '2.5rem',
                fontWeight: 900,
                letterSpacing: '-0.02em',
                lineHeight: '3.125rem',
            },
            'header-2': {
                fontSize: '2rem',
                fontWeight: 900,
                letterSpacing: '-0.02em',
                lineHeight: '2.5rem',
            },
            'header-3': {
                fontSize: '1.5rem',
                fontWeight: 900,
                letterSpacing: '-0.02em',
                lineHeight: '1.875rem',
            },
            'header-4': {
                fontSize: '1.125rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                lineHeight: '1.438rem',
            },
            'number-large': {
                fontSize: '2.5rem',
                fontWeight: 700,
                lineHeight: '3.125rem',
            },
            'number-medium': {
                fontSize: '1.875rem',
                fontWeight: 700,
                lineHeight: '2.375rem',
            },
            'number-small': {
                fontSize: '1.125rem',
                fontWeight: 700,
                lineHeight: '1.438rem',
            },
            'body-large-bold': {
                fontSize: '1rem',
                fontWeight: 700,
                letterSpacing: '0.02em',
                lineHeight: '1.375rem',
            },
            'body-large-semi-bold': {
                fontSize: '1rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                lineHeight: '1.375rem',
            },
            'body-large': {
                fontSize: '1rem',
                letterSpacing: '0.02em',
                lineHeight: '1.375rem',
            },
            'body-medium-bold': {
                fontSize: '0.875rem',
                fontWeight: 700,
                letterSpacing: '0.02em',
                lineHeight: '1.25rem',
            },
            'body-medium-semi-bold': {
                fontSize: '0.875rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                lineHeight: '1.25rem',
            },
            'body-medium': {
                fontSize: '0.875rem',
                letterSpacing: '0.02em',
                lineHeight: '1.25rem',
            },
            'body-small-bold': {
                fontSize: '0.813rem',
                fontWeight: 700,
                letterSpacing: '0.02em',
                lineHeight: '1.125rem',
            },
            'body-small-semi-bold': {
                fontSize: '0.813rem',
                fontWeight: 600,
                letterSpacing: '0.03em',
                lineHeight: '1.125rem',
            },
            'body-small': {
                fontSize: '0.813rem',
                letterSpacing: '0.03em',
                lineHeight: '1.125rem',
            },
            'pre-title': {
                fontSize: '0.75rem',
                letterSpacing: '0.06em',
                fontWeight: 600,
                lineHeight: '1.063rem',
            },
            'paragraph-large-bold': {
                fontSize: '1rem',
                fontWeight: 700,
                letterSpacing: '0.02em',
                lineHeight: '1.625rem',
            },
            'paragraph-large-semi-bold': {
                fontSize: '1rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                lineHeight: '1.625rem',
            },
            'paragraph-large': {
                fontSize: '1rem',
                letterSpacing: '0.02em',
                lineHeight: '1.625rem',
            },
            'paragraph-medium-bold': {
                fontSize: '0.875rem',
                fontWeight: 700,
                letterSpacing: '0.02em',
                lineHeight: '1.375rem',
            },
            'paragraph-medium-semi-bold': {
                fontSize: '0.875rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                lineHeight: '1.375rem',
            },
            'paragraph-medium': {
                fontSize: '0.875rem',
                letterSpacing: '0.02em',
                lineHeight: '1.375rem',
            },
            'paragraph-small-bold': {
                fontSize: '0.813rem',
                fontWeight: 700,
                lineHeight: '1.25rem',
            },
            'paragraph-small-semi-bold': {
                fontSize: '0.813rem',
                fontWeight: 600,
                lineHeight: '1.25rem',
            },
            'paragraph-small': {
                fontSize: '0.813rem',
                lineHeight: '1.25rem',
            },
        },
    },
    space: {
        none: '0px',
        '3xs': '4px',
        '2xs': '8px',
        xs: '12px',
        sm: '16px',
        md: '24px',
        base: '32px',
        lg: '40px',
        xl: '48px',
        '2xl': '56px',
        '3xl': '64px',
    },
    radii,
    colors,
    gradients,
    shadows: {
        none: 'none',
        'small-icon': '0px 2px 4px rgba(32, 39, 48, 0.16)',
        'icon-white': '0px 1px 0px #FFFFFF',
        dropdown: '0px 4px 8px rgba(32, 39, 48, 0.16)',
        card: '-4px -4px 4px 0px #FFFFFF, 3px 3px 6px 0px #7990A159',
        'card-hover': '-6px -6px 16px #FFFFFF, 6px 6px 16px rgba(32, 39, 48, 0.25)',
        tab: '-3px -3px 4px 2px #FFFFFF, 3px 3px 4px 1px rgba(32, 39, 48, 0.25)',
        'sticky-side-menu': '0px 1px 2px rgba(0, 0, 0, 0.15), 0px 4px 8px rgba(0, 0, 0, 0.2)',
        'chart-line': '0px 8px 6px rgba(77, 92, 112, 0.25)',
        'illustration-element': '-3px -3px 4px rgba(255, 255, 255, 0.36), 3px 3px 4px rgba(32, 39, 48, 0.24)',
        'illustration-blured-gray': '0px 1px 40px rgba(100, 151, 203, 0.16)',
        'mesh-line-vertical': '1px 0px 0px #FFFFFF',
        'mesh-line-horizontal': '0px 1px 0px #FFFFFF',
        inset: 'inset 2px 2px 2px rgba(121, 144, 161, 0.4)',
        'inner-90': 'inset 0px 2px 1px rgba(32, 39, 48, 0.16)',
        'inner-45': 'inset 1px 1px 1px rgba(32, 39, 48, 0.16)',
        'pie-chart-placeholder': 'inset 2px 2px 4px rgba(171, 188, 201, 0.25), inset 4px 4px 16px rgba(141, 150, 180, 0.4)',
        inner: 'inset 4px 4px 8px rgba(121, 144, 161, 0.16)',
    },
    breakpoints: {
        unit: 'px',
        values: {
            xl: 1240,
            lg: 905,
            md: 600,
            sm: 320,
        },
    },
    components: {
        modal: {
            background: 'light',
        },
        stepper: {
            background: {
                default: 'silver',
                active: 'primary',
            },
            border: {
                active: 'none',
                default: 'none',
            },
        },
        input: {
            size: {
                small: '32px',
                medium: '44px',
            },
            variant: 'body-large',
        },
        formField: {
            label: {
                color: 'graphite',
                variant: 'body-large',
            },
        },
        checkbox: {
            label: {
                color: 'graphite',
                variant: 'body-large',
            },
        },
        button: {
            sizes: {
                'extra-small': {
                    fontSize: '0.813rem',
                    lineHeight: '1.021rem',
                    letterSpacing: '0.03em',
                    fontWeight: 700,
                    height: '32px',
                    padding: '0 24px',
                    borderRadius: radii.lg,
                },
                small: {
                    fontSize: '0.875rem',
                    lineHeight: '1.1rem',
                    letterSpacing: '0.03em',
                    fontWeight: 700,
                    height: '38px',
                    padding: '0 24px',
                    borderRadius: radii.base,
                },
                medium: {
                    fontSize: '1rem',
                    lineHeight: '1.257rem',
                    letterSpacing: '0.03em',
                    fontWeight: 700,
                    height: '44px',
                    padding: '0 32px',
                    borderRadius: radii.base,
                },
                large: {
                    fontSize: '1rem',
                    lineHeight: '1.257rem',
                    letterSpacing: '0.03em',
                    fontWeight: 700,
                    height: '50px',
                    padding: '0 32px',
                    borderRadius: radii.base,
                },
            },
            variants: {
                primary: {
                    background: gradients.primary,
                    backgroundHovered: gradients['primary-hovered'],
                    color: colors.white,
                    colorHovered: colors.white,
                    colorFocused: colors.white,
                },
                secondary: {
                    background: colors.light,
                    backgroundHovered: gradients['primary-hovered'],
                    color: colors.sea,
                    colorHovered: colors.white,
                    colorFocused: colors.sea,
                },
                tertiary: {
                    background: colors.graphite,
                    backgroundHovered: colors.slathe,
                    color: colors.white,
                    colorHovered: colors.white,
                    colorFocused: colors.white,
                },
                upgrade: {
                    background: colors.sun,
                    backgroundHovered: colors.sunset,
                    color: colors.graphite,
                    colorHovered: colors.graphite,
                    colorFocused: colors.graphite,
                },
                ghost: {
                    background: 'transparent',
                    backgroundHovered: 'transparent',
                    color: colors.sea,
                    colorHovered: colors.sea,
                    colorFocused: colors.sea,
                },
            },
        },
    },
};
